import type { StockLevel, Warehouse } from '@erp/shared';
import { ArrowUpDown, Package, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, confirmDialog, FormField } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';
import { AdjustStockDialog } from '../inventory/AdjustStockDialog.js';

/** Opening stock typed while CREATING an item — applied right after it's saved. */
export interface OpeningStock {
  warehouseId: string;
  quantity: string;
  unitCost: string;
  /** Currency of `unitCost`; empty = the tenant base currency. */
  currencyCode: string;
}
export const EMPTY_OPENING: OpeningStock = { warehouseId: '', quantity: '', unitCost: '', currencyCode: '' };

/** True when the opening-stock form holds a quantity worth posting. */
export function hasOpeningStock(o: OpeningStock): boolean {
  const q = normalizeNumericInput(o.quantity).trim();
  return Boolean(o.warehouseId) && q !== '' && safeDecimal(q).gt(0);
}

interface MovementRow {
  id: string;
  warehouseCode: string;
  warehouseName: string;
  movementType: 'receipt' | 'shipment' | 'adjustment' | 'transfer_in' | 'transfer_out';
  quantity: string;
  unitCost: string | null;
  unitCostCurrencyCode: string | null;
  referenceType: string | null;
  occurredAt: string;
  note: string | null;
}

/** Only a manual adjustment may be deleted — everything else belongs to a document. */
export function movementRemovable(m: { movementType: string; referenceType: string | null }): boolean {
  return m.movementType === 'adjustment' && !m.referenceType;
}

interface Props {
  /** The saved item's id — null while the item is still being created. */
  itemId: string | null;
  item: { code: string; name: string };
  /** Create-mode opening stock (owned by the form so it can post it after save). */
  opening: OpeningStock;
  onOpeningChange: (next: OpeningStock) => void;
}

const selectCls = 'h-10 w-full rounded-md border border-border bg-bg px-3 text-sm text-fg outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20';

/**
 * The item's own Stock tab: what's on hand per warehouse, its movement history,
 * and a one-click way to put stock in or take it out — the same
 * `POST /stock/adjust` the Stock levels page uses, without leaving the item.
 *
 * While CREATING an item there is no id to hang stock on, so it collects an
 * OPENING quantity instead and the form posts it as soon as the item exists.
 */
export function ItemStockTab({ itemId, item, opening, onOpeningChange }: Props): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const canAdjust = usePermission('stock.adjust');
  const [warehouses, setWarehouses] = useState<Warehouse[] | null>(null);
  const [levels, setLevels] = useState<StockLevel[] | null>(null);
  const [movements, setMovements] = useState<MovementRow[] | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustWarehouse, setAdjustWarehouse] = useState('');

  useEffect(() => {
    void api()
      .get<{ items: Warehouse[] }>('/warehouses')
      .then((r) => setWarehouses(r.data.items.filter((w) => w.isActive)))
      .catch(() => setWarehouses([]));
  }, []);

  // Default the opening warehouse when the tenant has exactly one.
  useEffect(() => {
    if (!itemId && !opening.warehouseId && warehouses?.length === 1) {
      onOpeningChange({ ...opening, warehouseId: warehouses[0]!.id });
    }
  }, [itemId, opening, warehouses, onOpeningChange]);

  const reload = useCallback(async (): Promise<void> => {
    if (!itemId) return;
    const [lv, mv] = await Promise.allSettled([
      api().get<{ items: StockLevel[] }>('/stock/levels', { params: { itemId } }),
      api().get<{ items: MovementRow[] }>('/stock/movements', { params: { itemId, limit: 50 } }),
    ]);
    setLevels(lv.status === 'fulfilled' ? lv.value.data.items : []);
    setMovements(mv.status === 'fulfilled' ? mv.value.data.items : []);
  }, [itemId]);

  useEffect(() => { void reload(); }, [reload]);

  // ---- CREATE: no item id yet, so collect an opening quantity ----
  if (!itemId) {
    const qty = normalizeNumericInput(opening.quantity).trim();
    return (
      <Card className="p-6">
        <h2 className="mb-1 text-sm font-bold uppercase tracking-wide text-fg-muted">{t('itemStock.opening')}</h2>
        <p className="mb-4 text-sm text-fg-muted">{t('itemStock.openingHint')}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <FormField id="open-wh" label={t('stock.adjust.warehouse')}>
            <select
              id="open-wh"
              className={selectCls}
              value={opening.warehouseId}
              onChange={(e) => onOpeningChange({ ...opening, warehouseId: e.target.value })}
            >
              <option value="">{t('stock.adjust.selectWarehouse')}</option>
              {(warehouses ?? []).map((w) => (
                <option key={w.id} value={w.id}>{w.code} · {w.name}</option>
              ))}
            </select>
          </FormField>
          <FormField id="open-qty" label={t('stock.adjust.quantity')}>
            <input
              id="open-qty"
              dir="ltr"
              inputMode="decimal"
              autoComplete="off"
              className={selectCls}
              value={opening.quantity}
              onChange={(e) => onOpeningChange({ ...opening, quantity: normalizeNumericInput(e.target.value) })}
            />
          </FormField>
          <FormField id="open-cost" label={t('itemStock.openingCost')} helperText={t('itemStock.openingCostHint')}>
            <input
              id="open-cost"
              dir="ltr"
              inputMode="decimal"
              autoComplete="off"
              className={selectCls}
              value={opening.unitCost}
              onChange={(e) => onOpeningChange({ ...opening, unitCost: normalizeNumericInput(e.target.value) })}
            />
          </FormField>
          <FormField id="open-ccy" label={t('stock.adjust.currency')}>
            <CurrencySelect
              id="open-ccy"
              // Costs are kept in dollars: the default here, converted on save otherwise.
              value={opening.currencyCode || 'USD'}
              onChange={(code) => onOpeningChange({ ...opening, currencyCode: code })}
              className={selectCls}
              aria-label={t('stock.adjust.currency')}
            />
          </FormField>
        </div>
        {qty !== '' && !opening.warehouseId && (
          <p className="mt-3 text-sm text-danger" role="alert">{t('itemStock.openingNeedsWarehouse')}</p>
        )}
      </Card>
    );
  }

  // ---- EDIT: real stock ----
  const total = (levels ?? []).reduce((sum, l) => sum + Number(l.quantity), 0);
  const openAdjust = (warehouseId: string): void => {
    setAdjustWarehouse(warehouseId);
    setAdjustOpen(true);
  };

  // Delete a manual adjustment: the server reverses its quantity and takes its
  // value back out of the average, then drops the row. Destructive — confirm.
  const onRemoveMovement = async (m: MovementRow): Promise<void> => {
    const confirmed = await confirmDialog({
      title: t('movements.remove.title'),
      message: t('movements.remove.confirm', { quantity: m.quantity, warehouse: m.warehouseName }),
      tone: 'danger',
      confirmLabel: t('movements.remove.action'),
    });
    if (!confirmed) return;
    try {
      await api().delete(`/stock/movements/${m.id}`);
      toast.success(t('movements.remove.done'));
      await reload();
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      if (code === 'stock.insufficient') toast.error(t('movements.remove.errors.insufficient'));
      else if (code === 'movement.not_removable') toast.error(t('movements.remove.errors.notRemovable'));
      else toast.error(t('movements.remove.errors.failed'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wide text-fg-muted">{t('itemStock.title')}</h2>
            {levels && levels.length > 0 && (
              <p className="mt-1 text-sm text-fg-muted">
                {t('itemStock.total')}: <span className="font-mono font-semibold text-fg" dir="ltr">{total}</span>
              </p>
            )}
          </div>
          {canAdjust && (
            <Button type="button" size="sm" onClick={() => openAdjust('')}>
              <Plus className="me-1.5 h-4 w-4" aria-hidden />
              {t('itemStock.add')}
            </Button>
          )}
        </div>

        {!levels ? (
          <Skeleton className="h-24 w-full" />
        ) : levels.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <Package className="h-7 w-7 text-fg-subtle" aria-hidden />
            <p className="text-sm text-fg-muted">{t('itemStock.empty')}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-fg-subtle">
              <tr>
                <th className="py-1 text-start font-semibold">{t('stock.columns.warehouse')}</th>
                <th className="py-1 text-end font-semibold">{t('stock.columns.quantity')}</th>
                <th className="py-1 text-end font-semibold">{t('stock.columns.avgCost')}</th>
                <th className="py-1 text-start font-semibold">{t('stock.columns.updatedAt')}</th>
                {canAdjust && <th className="w-10 py-1" />}
              </tr>
            </thead>
            <tbody>
              {levels.map((l) => (
                <tr key={l.warehouseId} className="border-t border-border">
                  <td className="py-1.5">
                    <span className="font-mono text-xs">{l.warehouseCode}</span> · {l.warehouseName}
                  </td>
                  <td className="py-1.5 text-end font-mono tabular-nums">{trimMoney(l.quantity)}</td>
                  <td className="py-1.5 text-end font-mono tabular-nums">
                    {l.avgCostCurrencyCode
                      ? formatMoney(l.avgCost, { locale: i18n.language, currency: l.avgCostCurrencyCode })
                      : '—'}
                  </td>
                  <td className="py-1.5 text-fg-muted">{formatDateTime(l.updatedAt, { locale: i18n.language })}</td>
                  {canAdjust && (
                    <td className="py-1.5 text-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openAdjust(l.warehouseId)}
                        aria-label={`${t('stock.adjust.action')} ${l.warehouseCode}`}
                        title={t('stock.adjust.action')}
                      >
                        <ArrowUpDown className="h-4 w-4" aria-hidden />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-fg-muted">{t('movements.title')}</h2>
        {!movements ? (
          <Skeleton className="h-24 w-full" />
        ) : movements.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-muted">{t('movements.empty')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-fg-subtle">
              <tr>
                <th className="py-1 text-start font-semibold">{t('movements.columns.occurredAt')}</th>
                <th className="py-1 text-start font-semibold">{t('movements.columns.type')}</th>
                <th className="py-1 text-start font-semibold">{t('movements.columns.warehouse')}</th>
                <th className="py-1 text-end font-semibold">{t('movements.columns.quantity')}</th>
                <th className="py-1 text-end font-semibold">{t('movements.columns.unitCost')}</th>
                <th className="py-1 text-start font-semibold">{t('movements.columns.note')}</th>
                {canAdjust && <th className="w-10 py-1" />}
              </tr>
            </thead>
            <tbody>
              {movements.map((m) => (
                <tr key={m.id} className="border-t border-border">
                  <td className="py-1.5 text-fg-muted">{formatDateTime(m.occurredAt, { locale: i18n.language })}</td>
                  <td className="py-1.5">{t(`movements.type.${m.movementType}`)}</td>
                  <td className="py-1.5">
                    <span className="font-mono text-xs">{m.warehouseCode}</span> · {m.warehouseName}
                  </td>
                  <td className={`py-1.5 text-end font-mono tabular-nums ${Number(m.quantity) < 0 ? 'text-danger' : ''}`}>
                    {trimMoney(m.quantity)}
                  </td>
                  <td className="py-1.5 text-end font-mono tabular-nums">
                    {m.unitCost && m.unitCostCurrencyCode
                      ? formatMoney(m.unitCost, { locale: i18n.language, currency: m.unitCostCurrencyCode })
                      : '—'}
                  </td>
                  <td className="py-1.5 text-fg-muted">{m.note ?? '—'}</td>
                  {canAdjust && (
                    <td className="py-1.5 text-end">
                      {movementRemovable(m) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void onRemoveMovement(m)}
                          aria-label={t('movements.remove.action')}
                          title={t('movements.remove.action')}
                        >
                          <Trash2 className="h-4 w-4 text-danger" aria-hidden />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {canAdjust && (
        <AdjustStockDialog
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          levels={levels ?? []}
          initial={{ itemId, warehouseId: adjustWarehouse }}
          lockedItem={{ id: itemId, code: item.code, name: item.name }}
          onDone={reload}
        />
      )}
    </div>
  );
}
